import { FC, MouseEvent, useState } from 'react';
import clsx from 'clsx';
import { TreeItem as TreeItemType } from './types';
import { TreeItem } from './TreeItem';

interface TreeItemWithSubItemsProps<T = any> {
  item: TreeItemType<T>;
  level: 'main' | 'sub' | 'subSub';
  parentId?: string | number;
  grandParentId?: string | number;
  onToggleMain: (id: string | number) => void;
  onToggleSub: (mainId: string | number, subId: string | number) => void;
  onToggleSubSub: (mainId: string | number, subId: string | number, subSubId: string | number) => void;
  calculateSelectedCount?: (item: TreeItemType<T>) => number;
  renderItem?: (item: TreeItemType<T>, level: 'main' | 'sub' | 'subSub') => React.ReactNode;
  isMultiSelect: boolean;
}

export const TreeItemWithSubItems: FC<TreeItemWithSubItemsProps> = ({
  item,
  level,
  parentId,
  grandParentId,
  onToggleMain,
  onToggleSub,
  onToggleSubSub,
  calculateSelectedCount,
  renderItem,
  isMultiSelect,
}) => {
  // Track expanded state for this item
  const [expanded, setExpanded] = useState(true);

  // Handle toggle based on level
  const handleToggle = (e?: MouseEvent) => {
    // Prevent any default behavior that might cause page refresh
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (level === 'main') {
      onToggleMain(item.id);
    } else if (level === 'sub' && parentId !== undefined) {
      onToggleSub(parentId, item.id);
    } else if (level === 'subSub' && parentId !== undefined && grandParentId !== undefined) {
      onToggleSubSub(grandParentId, parentId, item.id);
    }
  };

  // Determine indentation and border styling based on level
  const getContainerClasses = () => {
    if (level === 'main') {
      return 'flex flex-col';
    }

    return 'ml-32 pl-16 border-l-2 border-neutral-20';
  };

  return (
    <div className={clsx('relative flex flex-col', level !== 'main' && 'pl-[28px] pt-4')}>
      <TreeItem
        item={item}
        level={level}
        onToggle={() => handleToggle()}
        expanded={expanded}
        onExpandToggle={() => setExpanded(!expanded)}
        renderItem={renderItem}
        isMultiSelect={isMultiSelect}
      />

      {/* Render sub-items if they exist */}
      {item.subItems && item.subItems.length > 0 && expanded && (
        <div className={getContainerClasses()}>
          {level === 'main' && (
            <div className='w-full'>
              {item.subItems.map((subItem) => (
                <TreeItemWithSubItems
                  key={subItem.id}
                  item={subItem}
                  level='sub'
                  parentId={item.id}
                  grandParentId={item.id}
                  onToggleMain={onToggleMain}
                  onToggleSub={onToggleSub}
                  onToggleSubSub={onToggleSubSub}
                  calculateSelectedCount={calculateSelectedCount}
                  renderItem={renderItem}
                  isMultiSelect={isMultiSelect}
                />
              ))}
            </div>
          )}

          {level === 'sub' && (
            <div>
              {item.subItems.map((subSubItem) => (
                <div key={subSubItem.id}>
                  <TreeItem
                    item={subSubItem}
                    level='subSub'
                    onToggle={() => onToggleSubSub(grandParentId!, item.id, subSubItem.id)}
                    expanded={false}
                    renderItem={renderItem}
                    isMultiSelect={isMultiSelect}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {expanded && item?.subItems?.length > 0 && <div className='absolute top-[42px] bottom-8 left-16 w-1 bg-neutral-20' />}
    </div>
  );
};
